import { Component, ElementRef, inject, OnDestroy } from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { PopoverService, PopoversState, ShortcutService, SolShortcut, SolShortcutSubscription } from '@sol/components';
import { LabPopoverComponent } from '../lab-popover/lab-popover.component';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { SolPopoversState } from './lab-popovers.state';

@Component({
  selector: 'lab-popovers',
  standalone: true,
  imports: [LabPopoverComponent, CdkTrapFocus],
  templateUrl: './lab-popovers.component.html',
  host: { 'data-shortcut-layer': 'popover' },
})
export class LabPopoversComponent extends AbstractComponent<SolPopoversState> implements OnDestroy {
  // Dependencies
  private readonly popoverService = inject(PopoverService);
  private readonly shortcutService = inject(ShortcutService);
  private readonly host = inject(ElementRef<HTMLElement>);

  // Cache
  private shortcutSubscription?: SolShortcutSubscription;

  // Computed
  protected readonly visible$ = this.compute(state => state.visible);
  protected readonly popovers$ = this.compute(state => state.popovers);

  constructor() {
    super(SolPopoversState.of());

    this.popoverService.watch(popoversState => this.updatePopovers(popoversState));
  }

  ngOnDestroy(): void {
    this.shortcutSubscription?.unsubscribe();
  }

  protected onGlassPaneMousedown(topId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.popoverService.clickOutside(topId);
  }

  private updatePopovers(popoversState: PopoversState): void {
    this.update(state => state.withPopovers(popoversState.popovers));
    this.syncShortcutRegistration(popoversState.popovers);
  }

  private syncShortcutRegistration(popovers: PopoversState['popovers']): void {
    if (popovers.length > 0) {
      if (!this.shortcutSubscription) {
        this.shortcutSubscription = this.shortcutService.register('sol-popovers', true, this.host, [
          { shortcut: SolShortcut.of('Escape'), action: () => this.onShortcutEscape() },
        ]);
      }
    } else {
      this.shortcutSubscription?.unsubscribe();
      this.shortcutSubscription = undefined;
    }
  }

  private onShortcutEscape(): boolean {
    const popovers = this.state.popovers;
    if (popovers.length === 0) return false;
    this.popoverService.clickOutside(popovers[popovers.length - 1].id);
    return true;
  }
}
